import { EmptyState, FlexColumn, MaterialIcon, useStyles } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { LoadingSpinner } from 'baseui/button/styled-components';
import { LabelLarge } from 'baseui/typography';
import { memo, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { environment } from '../../../environments/environment';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { deleteDeviceConnection, postStreamingCommand } from '../../../services/streamingService';
import { MinimalApiAsset } from '../../../types/asset';
import { ChannelId } from '../../../types/gptTypes';
import { FlespiItem, MediaStream, StreamingCommand, StreamingState, StreamType } from '../../../types/streaming';
import { fitDimensions } from '../../../utils/app';
import HlsPlayer from './HlsPlayer';
import MpegTsPlayer from './MpegTsPlayer';

type StreamingPlayerProps = Readonly<{
  asset: MinimalApiAsset;
  channel: ChannelId;
  playerRef: React.RefObject<HTMLVideoElement>;
  videoLayer?: React.ReactNode;
  state: StreamingState;
  canLoad?: boolean;
  onBadRequest: () => void;
  onDisconnect: () => void;
  onRefresh: () => void;
  onLoadEnd: () => void;
  streamType?: StreamType;
  poster?: string;
  muted?: boolean;
  maxWidth: number;
}>;

function isHlsMedia(mediaStream: string): mediaStream is MediaStream.hls {
  return mediaStream === MediaStream.hls;
}

function StreamingPlayer({
  asset,
  channel,
  playerRef,
  state,
  onDisconnect,
  onBadRequest,
  onRefresh,
  videoLayer,
  onLoadEnd,
  streamType = StreamType.main,
  canLoad,
  poster,
  muted = false,
  maxWidth,
}: StreamingPlayerProps) {
  const { css, theme, concatenateClasses } = useStyles();
  const [internalError, setInternalError] = useState(false);
  const [flespiItem, setFlespiItem] = useState<FlespiItem>();
  const isHls = isHlsMedia(environment.mediaStream);
  const streamUrl = flespiItem?.response.url;
  const persistVolume = useRef(muted ? 0 : 0.5);
  const isStuck = state === StreamingState.stuck;

  // Reset internal error when context changes
  useUpdateEffect(() => {
    setInternalError(false);
  }, [channel, state, asset.assetId, asset.clientId, asset.subClientId]);

  useEffect(() => {
    if (!canLoad) {
      return;
    }
    if (state !== StreamingState.ok) {
      setFlespiItem(undefined);
      return;
    }
    const loadUrl = async () => {
      const { streaming, error } = await postStreamingCommand({
        assetId: asset.assetId,
        command: StreamingCommand.startVideostream,
        channelId: channel,
        streamType: streamType,
        clientId: asset.clientId,
        subClientId: asset.subClientId,
        mediaStream: environment.mediaStream as MediaStream,
        audio: channel === ChannelId.internal,
      });
      const item = streaming ? streaming.flespiResponse.result[0] : undefined;
      if (error || item?.executed === false) {
        onBadRequest();
      } else {
        setFlespiItem(item);
      }
    };
    loadUrl();
  }, [asset.clientId, asset.subClientId, asset.assetId, channel, streamType, canLoad, state]);

  useEffect(() => {
    return () => {
      const closeConnection = async (connectionId: string) => {
        await deleteDeviceConnection({
          assetId: asset.assetId,
          connectionId,
          clientId: asset.clientId,
          subClientId: asset.subClientId,
        });
      };

      const connectionId = flespiItem?.response.connection_id?.toString();
      if (connectionId) {
        closeConnection(connectionId);
      }
    };
  }, [flespiItem?.response.connection_id]);

  const handleError = useCallback((event: SyntheticEvent<HTMLVideoElement, Event>) => {
    const error = event.currentTarget?.error;
    if (error) setInternalError(true);
  }, []);

  const handleOnDisconnect = async () => {
    if (flespiItem?.response.connection_id) {
      setFlespiItem(undefined);
    }
    onDisconnect();
  };

  const handleOnBadRequest = async () => {
    if (flespiItem?.response.connection_id) {
      setFlespiItem(undefined);
    }
    onBadRequest();
  };

  const PlayerContent = (
    <>
      {state !== StreamingState.ok && (
        <FlexColumn
          centered
          gap={theme.sizing.scale200}
        >
          {isStuck && (
            <MaterialIcon
              name={'videocam_off'}
              size={theme.sizing.scale800}
              color={theme.colors.contentInversePrimary}
            />
          )}
          <LabelLarge color={theme.colors.contentInversePrimary}>
            {isStuck ? 'Desconectado' : 'Conexión pausada'}
          </LabelLarge>
          <Button
            kind="secondary"
            size="mini"
            colors={{ color: 'white', backgroundColor: theme.colors.gray100Dark }}
            startEnhancer={
              <MaterialIcon
                name={isStuck ? 'sync' : 'play'}
                size={theme.sizing.scale600}
                color={theme.colors.contentInversePrimary}
              />
            }
            onClick={onRefresh}
          >
            {isStuck ? 'Refrescar' : 'Reanudar'}
          </Button>
        </FlexColumn>
      )}
      {state === StreamingState.ok && (
        <>
          {internalError && (
            <EmptyState
              style={{ background: theme.colors.backgroundPrimary, paddingBottom: theme.sizing.scale300 }}
              description="Ocurrió un error."
              action={
                <Button
                  shape="default"
                  kind="secondary"
                  size="mini"
                  onClick={() => setInternalError(false)}
                  startEnhancer={
                    <MaterialIcon
                      name="refresh"
                      size={theme.sizing.scale600}
                    />
                  }
                >
                  Recargar
                </Button>
              }
            />
          )}
          {!internalError && !streamUrl && <LoadingSpinner $size="medium" />}
          {!internalError && canLoad && streamUrl && !isHls && (
            <MpegTsPlayer
              className="player"
              src={streamUrl}
              autoPlay={true}
              controls
              playerRef={playerRef}
              onDisconnect={handleOnDisconnect}
              onBadRequest={handleOnBadRequest}
              onVolumeChange={(e) => (persistVolume.current = e.currentTarget.volume)}
              onLoadEnd={onLoadEnd}
              onError={handleError}
              style={{
                height: '100%',
                width: '100%',
                userSelect: 'none',
                cursor: 'default',
              }}
              poster={poster}
              initialVolume={persistVolume.current}
            />
          )}
          {!internalError && canLoad && streamUrl && isHls && (
            <HlsPlayer
              className="player"
              src={streamUrl}
              autoPlay={true}
              controls
              pauseOnPlay
              playerRef={playerRef}
              onBadRequest={handleOnBadRequest}
              onVolumeChange={(e) => (persistVolume.current = e.currentTarget.volume)}
              onLoadEnd={onLoadEnd}
              onError={handleError}
              style={{
                height: '100%',
                width: '100%',
                userSelect: 'none',
                cursor: 'default',
              }}
              poster={poster}
              initialVolume={persistVolume.current}
            />
          )}
        </>
      )}
      {videoLayer}
    </>
  );

  const { width: scaledWidth, height: scaledHeight } = fitDimensions({
    width: flespiItem?.response.width,
    height: flespiItem?.response.height,
    maxWidth,
  });

  // Live version uses AutoSizer (to fit container)
  return (
    <FlexColumn
      centered
      classNames={concatenateClasses(
        css({
          width: `${scaledWidth}px`,
          height: `${scaledHeight}px`,
          backgroundColor: theme.colors.backgroundInversePrimary,
          position: 'relative',
          borderRadius: theme.borders.radius300,
          overflow: 'hidden',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          backgroundImage: `url('${poster}')`,
          backgroundPosition: 'center',
        }),
      )}
    >
      {PlayerContent}
    </FlexColumn>
  );
}

export default memo(StreamingPlayer);
